import React, { FunctionComponent } from 'react';
import { useFormik } from 'formik';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { connect } from 'react-redux';
import { map, values as rValues } from 'ramda';
import { useSwipeable } from 'react-swipeable';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { confirmRelationshipValidationSchema } from '../../../../common/validation/yup';
import { IReferenceSurvey } from '../../../../common/models/reference/IReferenceSurvey';
import { IAppState } from '../../../../common/models/store/IAppState';
import RegularButton from '../../../../components/RegularButton';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import IconButton from '../../../../components/IconButton';
import Icon from '../../../../components/Icon';
import Input from '../../../../components/Input';
import { stringToDate } from '../../../../common/helpers/dateHelper';
import CustomSelect from '../../../../components/CustomSelect';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import { messages } from '../../../CandidateForm/containers/ReferenceCard/utils';

interface IProps {
  isConfirming: boolean;
  reference: IReferenceSurvey;
  candidateFirstName: string;
  isManualInput: boolean;
  goBack: IBindingAction;
  onSubmit: IBindingCallback1<IReferenceSurvey>;
}

type ConfirmInfoForm = Omit<IReferenceSurvey, 'workStartDate' | 'workEndDate'> & {
  workStartDate: string;
  workEndDate: string | null;
};

const ConfirmRelationshipForm: FunctionComponent<IProps> = ({
  isConfirming,
  reference,
  candidateFirstName,
  isManualInput,
  onSubmit,
  goBack
}) => {
  const initialValues: ConfirmInfoForm = {
    ...reference,
    workInCompany: reference.workInCompany || '',
    title: reference.title || '',
    workStartDate: reference.workStartDate ? moment(reference.workStartDate).format('MMyyyy') : '',
    workEndDate: reference.workEndDate ? moment(reference.workEndDate).format('MMyyyy') : ''
  };

  const submitForm = ({ workStartDate, workEndDate, ...rest }: ConfirmInfoForm) => {
    onSubmit({
      ...rest,
      workStartDate: stringToDate(workStartDate),
      workEndDate: workEndDate ? stringToDate(workEndDate) : null
    });
  };

  const { handleSubmit, setFieldValue, values, errors, isValid } = useFormik({
    initialValues,
    validateOnMount: true,
    onSubmit: submitForm,
    enableReinitialize: true,
    validationSchema: confirmRelationshipValidationSchema
  });

  const swipeHandler = useSwipeable({
    onSwipedLeft: () => handleSubmit(),
    onSwipedRight: () => (isManualInput ? null : goBack()),
    trackMouse: false
  });

  return (
    <LoaderWrapper transparent loading={isConfirming}>
      <div {...swipeHandler} className="survey-page">
        <div className="survey-form-container">
          <span className="survey-page-title mb-4">
            <FormattedMessage
              id="survey.confirmRelationship"
              defaultMessage="Confirm your relationship to {firstName}"
              values={{ firstName: candidateFirstName }}
            />
          </span>
          <div className="confirm-info-description">
            <span className="survey-page-text">
              <FormattedMessage
                id="survey.survey.confirmInfoDesc"
                defaultMessage={`{firstName} provided us the following information. Please edit / provide any missing 
                information and click “Next” when everything looks good! `}
                values={{
                  firstName: candidateFirstName
                }}
              />
            </span>
          </div>
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="survey-form">
              <div className="survey-inputs-row">
                <Input
                  size="lg"
                  onChange={val => setFieldValue('workInCompany', val)}
                  label={{
                    id: 'survey.refCompanyName',
                    defaultMessage: 'Where you worked with {firstName}',
                    values: { firstName: candidateFirstName }
                  }}
                  value={values.workInCompany as string}
                  variant="underlined"
                  error={Boolean(errors.workInCompany)}
                />
                <div>
                  <CustomSelect
                    label={{
                      id: 'survey.refRelationship',
                      defaultMessage: 'Your relationship to {firstName}',
                      values: { firstName: candidateFirstName }
                    }}
                    onChange={val => setFieldValue('type', val)}
                    value={values.type || undefined}
                    options={map(value => ({
                      label: messages[value as ReferenceType],
                      value
                    }), rValues(ReferenceType))}
                    variant="radio"
                    triggerVariant="underlined"
                    error={!values.type}
                  />
                  {values.type === ReferenceType.Other && (
                    <Input
                      type="text"
                      label={{
                        id: 'survey.refRelationship.other',
                        defaultMessage: 'Please specify your relationship to {candidate}',
                        values: { candidate: candidateFirstName }
                      }}
                      value={values.otherType as string}
                      onChange={val => setFieldValue('otherType', val)}
                      variant="underlined"
                      size="lg"
                      className="mt-4"
                      error={Boolean(errors.otherType)}
                    />
                  )}
                </div>
              </div>
              <div className="survey-inputs-row">
                <Input
                  type="date"
                  label={{
                    id: 'survey.startDate',
                    defaultMessage: 'When you worked with {firstName} (start)',
                    values: { firstName: candidateFirstName }
                  }}
                  value={values.workStartDate}
                  error={Boolean(errors.workStartDate)}
                  onChange={val => setFieldValue('workStartDate', val)}
                  variant="underlined"
                  size="lg"
                />
                <Input
                  type="date"
                  label={{
                    id: 'survey.endDate',
                    defaultMessage: 'When you worked with Jim (end - skip if current)',
                    values: { firstName: candidateFirstName }
                  }}
                  value={values.workEndDate as string}
                  error={Boolean(errors.workEndDate && values.workEndDate)}
                  onChange={val => setFieldValue('workEndDate', val)}
                  variant="underlined"
                  size="lg"
                />
              </div>
              <div className="survey-inputs-row">
                <Input
                  size="lg"
                  onChange={val => setFieldValue('title', val)}
                  label={{ id: 'survey.refTitle', defaultMessage: 'Your title during this time' }}
                  value={values.title as string}
                  variant="underlined"
                  error={Boolean(errors.title)}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <RegularButton
                size="lg"
                textIntlId="survey.next"
                defaultMessage="Next"
                variant="primary"
                onClick={handleSubmit}
                disabled={!isValid}
                icon={<Icon icon="arrowRight" />}
                iconPosition="right"
                type="submit"
              />
            </div>
          </form>
          {!isManualInput && (
            <div className="survey-back-bttn">
              <IconButton
                onClick={() => goBack()}
                icon={<Icon icon="arrowLeft" className="backButtonIcon" />}
                variant="bordered"
              />
            </div>
          )}
        </div>
      </div>
    </LoaderWrapper>
  );
};

const mapStateToProps = ({
  refSurvey: { reference, candidate: { firstName }, isConfirming, isManualInput }
}: IAppState) => ({ reference, candidateFirstName: firstName, isConfirming, isManualInput });

export default connect(mapStateToProps)(ConfirmRelationshipForm);
